import { Body, Heading, Sheet, calc, useToast } from '@hover/blueprint';
import React from 'react';

import { useAuthenticationInfoQuery } from 'src/shared/graphql/hooks';
import { StatusError } from 'src/shared/helpers/request';
import { createLead } from 'src/shared/requests/createLead';
import { buttonPressed } from 'src/shared/telemetry/generated/segment';
import { logErrorWithTags } from 'src/shared/telemetry/logger';

import { useProTranslate } from '../../hooks';
import { useProPageContext } from '../../hooks/useProPageContext';
import { ProPageSheetSteps } from '../../providers/ProPageProvider';
import type { LeadFormData } from '../LeadForm';
import { LeadForm } from '../LeadForm';

export const LeadSheet = ({
  onClose,
  photoUploadSheetOnOpen,
}: {
  onClose: () => void;
  photoUploadSheetOnOpen: () => void;
}) => {
  const t = useProTranslate();
  const toast = useToast();
  const {
    orgDisplayName,
    publicIdentifier,
    dispatchLeadResponse,
    loggedIn,
    setProPageSheetStep,
    genericTrackingData,
  } = useProPageContext();
  const [queryAuthenticationInfo] = useAuthenticationInfoQuery();

  const onSubmit = async (formData: LeadFormData) => {
    buttonPressed({
      ...genericTrackingData,
      button_text: 'Submit',
      button_location: 'try it yourself panel',
      track_id: 'get-in-touch-form-submit',
    });
    try {
      const leadData = await createLead(
        publicIdentifier,
        formData.email || '',
        formData.phoneNumber || '',
        formData.postalCode || '',
        formData.fullName || '',
      );

      dispatchLeadResponse(leadData);
      const {
        data: {
          authenticationInfo: { emailInUse },
        },
      } = await queryAuthenticationInfo({ email: formData.email || '' });

      if (loggedIn) {
        onClose();
        photoUploadSheetOnOpen();
      } else if (emailInUse) {
        setProPageSheetStep(ProPageSheetSteps.FOUND_ACCOUNT);
      } else {
        setProPageSheetStep(ProPageSheetSteps.CREATE_ACCOUNT);
      }
    } catch (error) {
      const errorStatusCode =
        error instanceof StatusError ? error.response.status : 500;

      if (errorStatusCode !== 429) {
        logErrorWithTags(error as Error, {
          featureName: 'LeadLandingPage',
        });
      }

      toast({
        description: t('leadForm.theresAnIssue'),
        position: 'top',
        props: { colorScheme: 'dark' },
        status: 'error',
      });
    }
  };

  return (
    <Sheet.Contents
      contentKey="lead-sheet"
      maxContentHeight={{
        base: calc.subtract('100vh', 'var(--motion-overflow)'),
        tablet: undefined,
      }}
    >
      <Body color="neutral.850" paddingTop={700} variant="semiBold">
        {t('leadSheet.stepOne')}
      </Body>
      <Heading size={{ base: 'large', tablet: 'xl' }}>
        {t('leadSheet.firstShareYourInformation', { orgDisplayName })}
      </Heading>
      <Body color="neutral.550">
        {t('leadSheet.aRepresentative', { orgDisplayName })}
      </Body>
      <LeadForm
        onSubmit={onSubmit}
        submitButtonText={t('leadSheet.continue')}
        trackingData={{
          ...genericTrackingData,
          button_location: 'try it yourself panel',
        }}
      />
    </Sheet.Contents>
  );
};
